// import MathManager from '@/utils/math-manager.utils'
import { HOLDER_TYPE_ENUM } from '@/enums/holder-type.enum'
import { currencyToNumber, percentageToNumber } from '@/filters'
import { isCnpjValid, isCpfValid } from '@/utils/formatCpfCnpj.utils'
import { isRefundTypePartial } from './enums/refund-type.enum'
import {
  SPLIT_ENUM,
  SPLIT_TYPE_ENUM,
  isAllTypeSplit,
  isSplitTypePercentage
} from './enums/splits.enum'

/* eslint-disable camelcase */
class BoletoCancelContainerManager {
  constructor (data) {
    this.data = data
  }

  static findRecipientNameBySplitId (split = [], recipientId) {
    return split.find(receiver => recipientId === receiver.recipient.id)
      ?.recipient.name
  }

  isTrue (value) {
    return ['yes', 'sim'].includes(value?.toLowerCase())
  }

  getHolderTypeBasedOnDocument (holderDocument) {
    if (isCpfValid(holderDocument)) return HOLDER_TYPE_ENUM.INDIVIDUAL
    else if (isCnpjValid(holderDocument)) return HOLDER_TYPE_ENUM.COMPANY
    else throw new Error(`Cpf Cnpj invalid ${holderDocument}`)
  }

  getRecipientNameBasedOnSplitId (recipientId) {
    if (Array.isArray(recipientId)) return SPLIT_ENUM.ALL

    return BoletoCancelContainerManager.findRecipientNameBySplitId(
      this.data.initialChargeSplit,
      recipientId
    )
  }

  prepareAmount (amount, type) {
    // we have to convert a number with 2 decimal places
    // to a integer, so we multiply the number by 1^(decimal places, in this case 2)
    const HUNDREDS = 1e2
    const convertedAmount = isSplitTypePercentage(type)
      ? percentageToNumber(amount) * HUNDREDS
      : currencyToNumber(amount) * HUNDREDS

    if (Number.isNaN(convertedAmount)) {
      return 0
    }

    return parseInt(convertedAmount)
  }

  prepareType (type) {
    return isSplitTypePercentage(type)
      ? SPLIT_TYPE_ENUM.PERCENTAGE.key
      : SPLIT_TYPE_ENUM.FLAT.key
  }

  prepareSplit (split) {
    // Only one recipient split id
    // ----
    // we have to choose based on the split
    // and send the entire object in the request body

    const parsedSplits = this.data.initialChargeSplit
      .filter(receiver => receiver?.recipient?.id === split)
      .map(receiver => {
        const type = this.prepareType(receiver.type)

        return {
          recipient_id: receiver?.recipient?.id,
          split_options: {
            charge_processing_fee: this.isTrue(
              receiver?.options?.charge_processing_fee
            ),
            charge_remainder_fee: this.isTrue(
              receiver?.options?.charge_remainder_fee
            ),
            liable: this.isTrue(receiver?.options?.liable)
          },
          type,
          amount:
            receiver?.recipient?.id === split
              ? this.prepareAmount(receiver.amount, type)
              : 0
        }
      })

    return parsedSplits
  }

  prepareDataForSubmission (password) {
    const { refundTypeValues, bankDetailsValues } = this.data
    const { amount, split } = refundTypeValues
    const {
      holderName: holder_name,
      holderDocument: holder_document,
      branchNumber: branch_number,
      branchCheckDigit: branch_check_digit,
      accountNumber: account_number,
      accountCheckDigit: account_check_digit,
      bank,
      type
    } = bankDetailsValues

    // INDIVIDUAL if holder document is cpf
    // COMPANY if holder document if cnpj
    const holder_type = this.getHolderTypeBasedOnDocument(holder_document)

    let data = {}

    data = {
      bank_account: {
        holder_name,
        holder_document,
        branch_number,
        branch_check_digit,
        account_number,
        holder_type,
        account_check_digit,
        bank,
        type
      },
      // remove the decimal places (2 decimal places), multiply by 100
      amount: parseFloat(amount.toFixed(2)),
      // password must be base64
      password: window.btoa(password)
    }

    if (this.shouldSendSplits()) {
      data.split = this.prepareSplit(split)
    }

    return data
  }

  shouldSendSplits () {
    if (
      !this.data.refundTypeValues.split ||
      this.data.refundTypeValues.split.length === 0
    ) {
      return
    }
    if (isAllTypeSplit(this.data.refundTypeValues.split)) {
      return
    }
    return (
      isRefundTypePartial(this.data.refundTypeValues.refundType) &&
      this.data.initialChargeSplit?.length > 1
    )
  }
}

export default BoletoCancelContainerManager
